import { useCallback, useEffect, useState } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { displayToastError } from "@ui/utils/displayToastError";
import { getParams } from "@ui/utils/getParams";

import { useMessageBus } from "@jti/fe-message-bus";
import { SearchApi } from "@jti/rest-api";
import { type SearchResponse } from "@jti/rest-api-types";

import type { UseSearch } from "../../../types";

export const useSearch = ({ type, locale, loadResultsPerPage = 10, initialResultsNumber = 20 }: UseSearch) => {
  const { dictionary } = useTranslations();
  const messageBus = useMessageBus();
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchResponse, setSearchResponse] = useState<{
    data: SearchResponse["data"];
    meta: SearchResponse["meta"] | null;
  }>({ data: [], meta: null });

  const getSearchResults = useCallback(
    async ({
      queryParam,
      perPage,
      page,
      isNewSearchQuery = false,
    }: {
      queryParam: string;
      perPage: number;
      page: number;
      isNewSearchQuery?: boolean;
    }) => {
      setIsLoading(true);
      messageBus.publish("search.total.results", {
        payload: { isResultsLoading: true, searchTotalResults: searchResponse.meta?.total ?? 0 },
      });
      try {
        const { data, meta } = await SearchApi.init().getSearchResults({ queryParam, perPage, page, locale, type });

        messageBus.publish("search.total.results", {
          payload: { searchTotalResults: meta?.total, isResultsLoading: false },
        });

        setSearchResponse((prevResponse) => ({
          data: isNewSearchQuery ? data : [...prevResponse.data, ...data],
          meta,
        }));
      } catch (e) {
        console.warn("Error fetching search results", e);
        displayToastError(e, undefined, dictionary);
      } finally {
        setIsLoading(false);
      }
    },
    [type, locale]
  );

  const loadMoreHandler = useCallback(async () => {
    const searchPage =
      searchResponse.meta?.current_page === 1
        ? Math.max(1, Math.ceil(searchResponse?.data?.length / loadResultsPerPage)) + 1
        : (searchResponse.meta?.current_page ?? 0) + 1;

    await getSearchResults({
      queryParam: searchText,
      perPage: loadResultsPerPage,
      page: searchPage,
    });
  }, [
    searchResponse?.meta?.current_page,
    searchResponse?.data?.length,
    getSearchResults,
    searchText,
    loadResultsPerPage,
  ]);

  const searchByParam = useCallback(async () => {
    const { q } = getParams("q");

    if (q) {
      setSearchText(q);
      await getSearchResults({ queryParam: q, perPage: initialResultsNumber, page: 1, isNewSearchQuery: true });
    }
  }, [setSearchText, getSearchResults, initialResultsNumber]);

  useEffect(() => {
    searchByParam();
    window.addEventListener("popstate", searchByParam);

    return () => {
      window.removeEventListener("popstate", searchByParam);
    };
  }, [searchByParam]);

  useEffect(() => {
    messageBus.subscribe({
      channel: "search.value",
      callback() {
        searchByParam();
      },
    });

    return () => {
      messageBus.unsubscribe("search.value");
    };
  }, [messageBus, searchByParam]);

  return {
    isLoading,
    searchText,
    searchResponse,
    loadMoreHandler,
  };
};
